/**
 * Service Layer - Dynamic Implementation
 * 
 * Dynamic API service that chooses between real and mock API based on current environment configuration.
 * API selection is evaluated on each call, allowing for runtime environment changes.
 */

import { api as mockApi } from './api'
import { dynamicApiClient } from './dynamicApiClient'
import type { Customer, LeaderboardEntry, Reward } from '../types'

// Cypress window extensions interface
interface CypressWindow extends Window {
  Cypress?: {
    env: (key: string) => string | undefined
  }
}

// Type assertion helper for window with Cypress
const getCypressWindow = (): CypressWindow => window as CypressWindow

/**
 * Get environment variable - HARDCODED TO PRODUCTION VALUES
 */
const getEnvVar = (key: string): string | undefined => {
  // HARDCODED: Return production backend URL
  if (key === 'VITE_API_BASE_URL') {
    return 'https://backend.apex-cognition.com'
  }
  
  // HARDCODED: Never use mock API
  if (key === 'VITE_USE_MOCK_API') {
    return 'false'
  }
  
  // Fall back to Vite environment variables for other keys
  return import.meta.env[key]
}

/**
 * Determine which API to use - HARDCODED TO ALWAYS USE REAL API
 */
const shouldUseMockApi = (): boolean => {
  // HARDCODED: Always use real API
  return false
}

/**
 * Get the appropriate API client dynamically
 * This function evaluates environment variables on each call
 */
const getApiClient = () => {
  return shouldUseMockApi() ? mockApi : dynamicApiClient
}

/**
 * Dynamic API service that creates method wrappers for runtime API selection
 */
const createDynamicApi = () => {
  return {
    user: {
      getCurrentUser: (): Promise<Customer | null> => getApiClient().user.getCurrentUser(),
      getAvailableUsers: (): Promise<Customer[]> => getApiClient().user.getAvailableUsers(),
      switchCurrentUser: (customerId: string): Promise<boolean> => getApiClient().user.switchCurrentUser(customerId),
      updatePoints: (points: number): Promise<boolean> => getApiClient().user.updatePoints(points),
      getCustomersByIds: (customerIds: string[]): Promise<Customer[]> => getApiClient().user.getCustomersByIds(customerIds),
    },
    rewards: {
      getRewards: (): Promise<Reward[]> => getApiClient().rewards.getRewards(),
      getQuests: (): Promise<Reward[]> => getApiClient().rewards.getQuests(),
      updateReward: (reward: Reward): Promise<boolean> => getApiClient().rewards.updateReward(reward),
      redeemReward: (rewardId: string, pointsToSpend: number): Promise<{ success: boolean; customer?: Customer; reward?: Reward }> => 
        getApiClient().rewards.redeemReward(rewardId, pointsToSpend),
    },
    leaderboard: {
      getLeaderboard: (): Promise<LeaderboardEntry[]> => getApiClient().leaderboard.getLeaderboard(),
      getFocusedLeaderboardData: (currentUserId: string): Promise<{ topThree: LeaderboardEntry[]; focusedView: LeaderboardEntry[] }> => 
        getApiClient().leaderboard.getFocusedLeaderboardData(currentUserId),
    },
  }
}

/**
 * Main API service - dynamic environment-based selection
 */
export const api = createDynamicApi()

/**
 * Export both clients for explicit usage if needed
 */
export { mockApi, dynamicApiClient as realApiClient }

/**
 * Export utility functions for testing and debugging
 */
export { getEnvVar, shouldUseMockApi, getApiClient }

// HARDCODED: Always log that we're using production API
console.log('🔌 HARDCODED: Using Production API at https://backend.apex-cognition.com')